import { useState } from "react";
import { useLang } from "@/contexts/LangContext";
import SectionReveal from "@/components/SectionReveal";
import { GraduationCap, BookOpen, Calendar, Users, MapPin, Check, ArrowRight } from "lucide-react";

const WHATSAPP = "971589656886";

const PROGRAMS = [
  {
    id: "mandarin",
    tag: "Program 01 · Language Immersion",
    name: "Chinese Language Immersion Program",
    tagline: "Learn Chinese in China. Experience China beyond the classroom.",
    location: "Tangshan, China",
    venue: "Tangshan Maritime Vocational College",
    image: "/img/services/calligraphy.png",
    highlights: [
      "Chinese language classes (beginner → advanced)",
      "HSK 1-6 exam preparation",
      "Daily real-life Chinese communication practice",
      "Culture workshops: calligraphy, tea ceremony, cooking",
      "Student exchange & buddy program",
      "Excursions to Beijing & Tianjin",
    ],
    formats: ["2 Weeks", "4 Weeks", "Summer Camp", "Winter Camp"],
    audience: ["International Students", "Young Professionals", "Schools & Universities", "Families"],
    accent: "from-[#C62828] to-[#8E1A1A]",
  },
  {
    id: "jidong",
    tag: "Program 02 · Cultural Discovery",
    name: "Jidong Cultural Discovery Program",
    tagline: "Discover the heritage, culture and spirit of modern China.",
    location: "Tangshan, Hebei Province",
    venue: "Multi-site cultural & industrial visits",
    image: "/img/destinations/xian.png",
    highlights: [
      "Chinese history & Jidong heritage deep-dives",
      "Traditional arts: ceramics, paper-cutting, opera",
      "Chinese family homestay experience",
      "Industrial & innovation site visits",
      "Local community engagement projects",
      "Cultural exchange ceremonies & performances",
    ],
    formats: ["7 Days", "10 Days", "14 Days"],
    audience: ["International Schools", "Educational Institutions", "Cultural Exchange Groups", "Student Delegations"],
    accent: "from-[#2E5D4B] to-[#16291F]",
  },
];

const Education = () => {
  const { t } = useLang();
  const [active, setActive] = useState("mandarin");
  const program = PROGRAMS.find((p) => p.id === active);

  const whatsappLink = (p) =>
    `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(
      `Hi The China Explorer! We're interested in the "${p.name}" in ${p.location}. Please share dates, pricing and a proposal.`
    )}`;

  return (
    <section
      id="education"
      data-testid="education-section"
      className="bg-[#F7F5F0] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionReveal className="max-w-3xl">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C62828] font-semibold mb-4">
            ✦ Education Programs · Flagship Offering
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#121212] leading-[1.05]">
            Study, immerse, transform.
          </h2>
          <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed">
            More than a trip — structured educational journeys in partnership with Chinese
            colleges and cultural institutions, designed for international students, schools
            and delegations.
          </p>
        </SectionReveal>

        {/* Program selector tabs */}
        <SectionReveal delay={0.1} className="mt-12 flex flex-wrap gap-3">
          {PROGRAMS.map((p) => (
            <button
              key={p.id}
              data-testid={`education-tab-${p.id}`}
              onClick={() => setActive(p.id)}
              className={`px-5 py-3 uppercase tracking-wider text-xs font-semibold transition-all ${
                active === p.id
                  ? "bg-[#0E2A5C] text-white"
                  : "bg-white border border-black/15 text-[#4A4A4A] hover:border-[#0E2A5C]"
              }`}
            >
              {p.tag.split("·")[0].trim()} — {p.name.split(" Program")[0]}
            </button>
          ))}
        </SectionReveal>

        {/* Active program card */}
        <SectionReveal delay={0.15} className="mt-10">
          <div className={`relative overflow-hidden bg-gradient-to-br ${program.accent} text-white`}>
            <div
              className="absolute inset-0 opacity-15 bg-cover bg-center"
              style={{ backgroundImage: `url(${program.image})` }}
            />
            <div className="relative grid lg:grid-cols-12 gap-10 p-8 md:p-14">
              <div className="lg:col-span-7">
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">
                  {program.tag}
                </p>
                <h3 className="font-heading text-3xl md:text-5xl leading-tight">
                  {program.name}
                </h3>
                <p className="mt-4 text-lg text-white/85 italic font-heading">
                  "{program.tagline}"
                </p>

                <div className="mt-7 flex flex-wrap gap-5 text-sm">
                  <span className="inline-flex items-center gap-2 text-white/80">
                    <MapPin size={14} className="text-[#D4AF37]" /> {program.location}
                  </span>
                  <span className="inline-flex items-center gap-2 text-white/80">
                    <BookOpen size={14} className="text-[#D4AF37]" /> {program.venue}
                  </span>
                </div>

                <p className="mt-8 text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                  Program highlights
                </p>
                <ul className="mt-3 grid sm:grid-cols-2 gap-y-2.5 gap-x-6">
                  {program.highlights.map((h, j) => (
                    <li key={j} className="text-sm text-white/90 flex items-start gap-2">
                      <Check size={15} className="text-[#3BFFC2] mt-0.5 flex-shrink-0" />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:col-span-5 space-y-6">
                <div className="bg-white/[0.08] border border-white/15 p-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold flex items-center gap-2">
                    <Calendar size={14} /> Available formats
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {program.formats.map((f) => (
                      <span
                        key={f}
                        className="bg-white text-[#0E2A5C] px-3 py-1.5 text-xs uppercase tracking-wider font-bold"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="bg-white/[0.08] border border-white/15 p-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold flex items-center gap-2">
                    <Users size={14} /> Designed for
                  </p>
                  <ul className="mt-3 space-y-1.5">
                    {program.audience.map((a) => (
                      <li key={a} className="text-sm text-white/85">• {a}</li>
                    ))}
                  </ul>
                </div>

                <a
                  data-testid={`education-${program.id}-cta`}
                  href={whatsappLink(program)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-[#D4AF37] hover:bg-[#bf9a2b] text-[#0E2A5C] px-7 py-4 uppercase tracking-wider text-sm font-bold transition-colors inline-flex items-center justify-center gap-2"
                >
                  <GraduationCap size={16} /> Request Program Brochure
                  <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </div>
        </SectionReveal>
      </div>
    </section>
  );
};

export default Education;
